// src/components/messages/EventPickerInline.jsx
// Phase 2 of the smart-composer redesign — a compact inline picker that
// sits at the top of a wizard's "details" step and offers up to 8 events
// the coach is most likely trying to message about.
//
// The picker fetches its own candidates (so the wizards stay clean of
// query glue). Tapping a card calls onSelect(eventId); the parent looks
// up the matching candidate object, runs deriveDetailsForType, and
// merges the result into its form state.
//
// Visual language matches NotifyComposerSlideOver: lime accents, pitch
// text, white cards on zinc-200 borders.
//
// Empty / loading rules:
//   • loading                  → single zinc skeleton row
//   • loaded but no candidates → "No matching events" line, NO picker shell
//                                 (so the form below isn't pushed down for
//                                  no reason)
//   • loaded with candidates   → list of cards + "Skip — fill manually" link

'use client';
import React, { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { format } from 'date-fns';
import { SparklesIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

import { fetchEventCandidatesForMessageType } from '@/lib/eventCandidates';

const HEAD_FONT = "'Barlow Condensed', sans-serif";

/** Same date helper used in messagePrefill — Firestore Timestamp → JS Date. */
function toDate(v) {
  if (!v) return null;
  if (typeof v?.toDate === 'function') return v.toDate();
  if (typeof v?.seconds === 'number') return new Date(v.seconds * 1000);
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Pretty date — "Sat May 4" — short enough to fit on one line. */
function formatShortDate(d) {
  if (!d) return '';
  try { return format(d, 'EEE MMM d'); } catch { return ''; }
}

/** Score chip text from the team's perspective. */
function scoreChip(c) {
  if (c.homeScore == null || c.awayScore == null) return null;
  const isHome = c.homeAway === 'home';
  const ours = isHome ? c.homeScore : c.awayScore;
  const theirs = isHome ? c.awayScore : c.homeScore;
  if (ours > theirs) return { label: `Won ${ours}-${theirs}`, tone: 'win' };
  if (ours < theirs) return { label: `Lost ${ours}-${theirs}`, tone: 'loss' };
  return { label: `Tied ${ours}-${theirs}`, tone: 'tie' };
}

function ChipScore({ chip }) {
  if (!chip) return null;
  const cls = chip.tone === 'win'
    ? 'bg-emerald-100 text-emerald-700 border-emerald-200'
    : chip.tone === 'loss'
      ? 'bg-red-100 text-red-700 border-red-200'
      : 'bg-zinc-100 text-zinc-700 border-zinc-200';
  return (
    <span
      className={clsx('inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide', cls)}
      style={{ fontFamily: HEAD_FONT, letterSpacing: '0.5px' }}
    >
      {chip.label}
    </span>
  );
}

/**
 * @param {object} props
 * @param {string[]} props.teamIds       — one team for ComposerWizard, many for ClubBroadcast
 * @param {string}   props.messageType   — must match a MESSAGE_TYPES.id
 * @param {string|null} props.selectedId — currently picked event id (or null)
 * @param {(eventId: string, candidate: object) => void} props.onSelect
 * @param {() => void} props.onSkip
 */
export default function EventPickerInline({
  teamIds,
  messageType,
  selectedId,
  onSelect,
  onSkip,
}) {
  const [loading, setLoading] = useState(true);
  const [candidates, setCandidates] = useState([]);

  // Stable key for the effect — reordering the same set of teamIds
  // shouldn't refire the query. Same trick the rest of the app uses
  // for Firestore-driven hooks.
  const teamKey = useMemo(() => (teamIds || []).slice().sort().join(','), [teamIds]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setCandidates([]);
    (async () => {
      try {
        const list = await fetchEventCandidatesForMessageType({
          teamIds: teamIds || [],
          messageType,
          max: 8,
        });
        if (!cancelled) setCandidates(list);
      } catch (err) {
        if (import.meta.env?.DEV) console.warn('[EventPickerInline] fetch failed', err);
        if (!cancelled) setCandidates([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamKey, messageType]);

  // ── Loading state — single skeleton row inside the lime-accented shell ──
  if (loading) {
    return (
      <div className="rounded-2xl border border-[#b5f03d]/40 bg-[#b5f03d]/5 p-4 mb-4">
        <div className="flex items-center gap-2 mb-3">
          <SparklesIcon className="h-4 w-4 text-[#5a8a1f]" />
          <span
            className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
            style={{ fontFamily: HEAD_FONT }}
          >
            Pre-fill from an event
          </span>
        </div>
        <div className="h-12 w-full rounded-xl bg-zinc-100 animate-pulse" />
      </div>
    );
  }

  // ── Empty state — collapse to a single-line hint, no picker shell ──
  // The wizard form below already gives the coach a way to fill manually,
  // so we don't waste vertical space here.
  if (candidates.length === 0) {
    return (
      <p className="mb-4 text-xs text-zinc-500">
        No matching events found in the last 21 days. Fill in the form below.
      </p>
    );
  }

  return (
    <div className="rounded-2xl border border-[#b5f03d]/40 bg-[#b5f03d]/5 p-4 mb-4">
      <div className="flex items-center gap-2 mb-3">
        <SparklesIcon className="h-4 w-4 text-[#5a8a1f]" />
        <span
          className="text-[11px] font-bold uppercase tracking-[1.4px] text-[#5a8a1f]"
          style={{ fontFamily: HEAD_FONT }}
        >
          Pre-fill from an event
        </span>
      </div>

      <div className="space-y-2">
        {candidates.map((c) => {
          const isSelected = selectedId === c.id;
          const date = toDate(c.date);
          const dateStr = formatShortDate(date);
          const isGame = c.eventType === 'game';
          const chip = isGame ? scoreChip(c) : null;

          // Title line — game vs opponent, otherwise "Practice", "Tournament", etc.
          const titlePieces = [];
          if (isGame) {
            titlePieces.push(c.opponent ? `vs ${c.opponent}` : 'Game');
          } else {
            const t = String(c.eventType || 'event');
            titlePieces.push(t.charAt(0).toUpperCase() + t.slice(1));
          }
          if (dateStr) titlePieces.push(dateStr);
          if (c._teamName) titlePieces.push(c._teamName);

          // Subtitle — location for non-game events; field name for games
          // when known. Skipped if there's nothing useful.
          const subtitleBits = [];
          if (!isGame && c.fieldName) subtitleBits.push(c.fieldName);
          if (isGame && c.fieldName)  subtitleBits.push(c.fieldName);
          const subtitle = subtitleBits.join(' · ');

          return (
            <button
              key={c.id}
              type="button"
              onClick={() => onSelect(c.id, c)}
              className={clsx(
                'w-full text-left rounded-xl border bg-white shadow-[0_1px_2px_0_rgba(13,31,20,0.04)] px-3 py-2.5 transition-all',
                isSelected
                  ? 'border-[#b5f03d] bg-[#b5f03d]/10 ring-2 ring-[#b5f03d]/30'
                  : 'border-zinc-200 hover:border-[#b5f03d]/40',
              )}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-[#0d1f14] truncate">
                    {titlePieces.join(' · ')}
                  </p>
                  {subtitle && (
                    <p className="text-xs text-zinc-500 mt-0.5 truncate">{subtitle}</p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {chip && <ChipScore chip={chip} />}
                  {isSelected && <CheckCircleIcon className="h-5 w-5 text-[#5a8a1f]" />}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div className="mt-3 text-right">
        <button
          type="button"
          onClick={onSkip}
          className="text-[11px] font-semibold text-zinc-500 hover:text-[#5a8a1f] underline underline-offset-2"
        >
          Skip — fill manually
        </button>
      </div>
    </div>
  );
}
